import React from 'react';
import { Typography, Paper, Box, Link as MuiLink } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';

// --- Elevated Flat Brand Palette (Fresher Version) ---
const BRAND_PRIMARY_TEXT = '#0b5295'; // Slightly lighter, richer Navy Blue
const UI_CANVAS_BACKGROUND = '#fcfcfc'; // Whiter screen background
const UI_CARD_BACKGROUND = '#ffffff'; // White background for AI messages
const UI_SHADOW = '0 1px 4px rgba(0, 0, 0, 0.05)'; // Lighter subtle shadow

// 2. The main message bubble style (Refined Radius)
const MessageBubble = styled(Paper)(({ theme, isUser }) => ({
    padding: theme.spacing(1.5),
    maxWidth: '100%',
    boxShadow: UI_SHADOW, 
    lineHeight: 1.5, // Better line spacing for readability
    
    // Default (Assistant/AI Message)
    backgroundColor: UI_CARD_BACKGROUND,
    color: BRAND_PRIMARY_TEXT,
    borderRadius: '18px 18px 18px 8px', 
    
    // User Specific Overrides
    ...(isUser && {
        backgroundColor: BRAND_PRIMARY_TEXT, // Navy Blue
        color: theme.palette.common.white,
        borderRadius: '18px 18px 8px 18px', 
    }),

    wordBreak: 'break-word',
    transition: 'background-color 0.2s',

    // Styles for list elements generated by the parser
    '& ul, & ol': {
        paddingLeft: theme.spacing(3), // Increased padding for list marker visibility
        margin: theme.spacing(1, 0),
    },
    
    // Target the paragraphs and list items generated by the renderer
    '& p, & li': {
        margin: theme.spacing(0.5, 0), // Spacing between points/paragraphs
    },

    // Inline code styling (kept for safety)
    '& code': {
        backgroundColor: isUser ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.05)',
        padding: '2px 4px',
        borderRadius: '4px',
        fontFamily: 'monospace',
    },
    
    // Link styling
    '& a': {
        color: isUser ? theme.palette.common.white : BRAND_PRIMARY_TEXT,
        textDecoration: 'underline',
        fontWeight: 'bold',
        // Ensure links within the user bubble are visible
        filter: isUser ? 'brightness(1.2)' : 'none',
    }
}));


// Helper function to parse plain text/simple markdown into React elements
const renderMessageContent = (content) => {
    if (!content) return null;

    // Regex to find [Text](URL) format and **bold text**
    const linkRegex = /\[(.*?)\]\((.*?)\)/g;
    const boldRegex = /\*\*(.*?)\*\*/g;
    
    const lines = content.split('\n');
    let elements = [];
    let currentList = null;

    const processLine = (line, index, isListItem = false) => {
        let htmlContent = line;

        // 1. Handle Links [Text](URL)
        htmlContent = htmlContent.replace(linkRegex, (match, text, url) => {
            // Using target="_blank" for external links
            return `<a href="${url}" target="_blank" rel="noopener noreferrer">${text}</a>`;
        });
        
        // 2. Handle Bold **Text**
        htmlContent = htmlContent.replace(boldRegex, (match, text) => {
            return `<strong>${text}</strong>`;
        });

        // Use dangerouslySetInnerHTML to render the text and the generated anchor/strong tags
        return (
            <Typography 
                component={isListItem ? 'li' : 'p'} 
                variant="body1" 
                key={index} 
                dangerouslySetInnerHTML={{ __html: htmlContent }} 
                // Ensure list items render correctly inside OL/UL component
                sx={{ margin: isListItem ? '0' : undefined }} 
            />
        );
    };


    lines.forEach((line, index) => {
        const trimmedLine = line.trim();
        
        // Check for list item syntax (1., *, -)
        const listItemMatch = trimmedLine.match(/^(\d+\.|\*|-)\s(.*)/);

        if (listItemMatch) {
            const listMarker = listItemMatch[1];
            const itemText = listItemMatch[2];
            const listType = listMarker.match(/\d+\./) ? 'ol' : 'ul';

            // Check if we need to close the previous list or start a new type
            if (!currentList || currentList.props.component !== listType) {
                if (currentList) {
                    elements.push(currentList); 
                }
                // Start a new list structure (Box component acts as the wrapper)
                currentList = (
                    <Box component={listType} key={`list-start-${index}`} sx={{ m: 0, p: 0 }}>
                        {/* children will store the list items */}
                        {[]} 
                    </Box>
                );
            }
            
            // Create the new list item element
            const newItem = processLine(itemText, index, true);
            
            // Add the new item to the cloned list children
            currentList = React.cloneElement(currentList, {
                children: [...currentList.props.children, newItem]
            });

        } else {
            // Not a list item
            if (currentList) {
                elements.push(currentList); // Close previous list
                currentList = null;
            }
            
            if (trimmedLine.length > 0) {
                 // Add as a paragraph only if the line is not empty
                elements.push(processLine(trimmedLine, index));
            }
        }
    });

    // Push any remaining open list
    if (currentList) {
        elements.push(currentList);
    }
    
    return elements;
};


const Message = ({ message, sender }) => {
    const isUser = sender === 'user';

    // We do not handle code blocks anymore, only rich text markdown (P, Lists, Links)
    const contentElements = renderMessageContent(message);

    return (
        // Note: CodeBlock component is removed as there is no need for it now.
        <MessageBubble isUser={isUser} elevation={1}> 
            {contentElements}
        </MessageBubble>
    );
};

export default Message;